import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EmployeePickerField from "../../../../common/selectComponents/EmployeePicker";
import { useGenerateFixedLogs } from "../../../../v2/hooks/useGenerateFixedLogs";
import { DateInput } from "../../../../common/Input"; // Adjusted import based on your project structure

const GenerateEmployeeFixedLogs = ({ isOpen, onClose, user, showGeneralMessage }) => {
  const { generateFixedLogsByEmployee, loading } = useGenerateFixedLogs();

  const [form, setForm] = useState({
    employeePersonalInformationId: "",
    employeeName: "",
    dateFrom: "",
    dateTo: "",
  });

  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!form.employeePersonalInformationId) newErrors.employeePersonalInformationId = "Employee is required";
    if (!form.dateFrom) newErrors.dateFrom = "Start date is required";
    if (!form.dateTo) newErrors.dateTo = "End date is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGenerate = async () => {
    if (!validate()) return;

    const result = await generateFixedLogsByEmployee(
      form.employeePersonalInformationId,
      form.dateFrom,
      form.dateTo,
      user?.id
    );

    if (result.success) {
      showGeneralMessage(result.message || "Logs generated successfully", true);
      onClose();
    } else {
      // This displays the "Please set Cutoff Count..." error from your C# backend
      showGeneralMessage(result.message || "Failed to generate logs", false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-lg shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex justify-between rounded-t-lg items-center p-4 border-b bg-gray-50">
          <h3 className="font-bold text-gray-600 flex items-center gap-2">
            <CalendarMonthIcon className="text-green-600" />
            Generate Fixed Logs
          </h3>
          <button onClick={onClose} className="bg-transparent border-none cursor-pointer text-gray-500 hover:text-gray-700">
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <div>
            <EmployeePickerField
              label="Select Employee"
              isRequired={true}
              employeePersonalInformationId={form.employeePersonalInformationId}
              displayValue={form.employeeName}
              onSelect={(emp) => {
                setForm({ ...form, employeePersonalInformationId: emp.id, employeeName: emp.fullName });
                setErrors((prev) => ({ ...prev, employeePersonalInformationId: "" }));
              }}
            // Check if your EmployeePicker handles error prop similarly, otherwise keep current logic
            />
            {errors.employeePersonalInformationId && (
              <p className="text-red-500 text-xs mt-1">{errors.employeePersonalInformationId}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Reusable DateInput for 'Date From' */}
            <DateInput
              label="Date From"
              name="dateFrom"
              isRequired={true}
              value={form.dateFrom}
              onChange={handleChange}
              error={!!errors.dateFrom}
              errorMessage={errors.dateFrom}
            />

            {/* Reusable DateInput for 'Date To' */}
            <DateInput
              label="Date To"
              name="dateTo"
              isRequired={true}
              value={form.dateTo}
              onChange={handleChange}
              error={!!errors.dateTo}
              errorMessage={errors.dateTo}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-400 text-white rounded border-none cursor-pointer hover:bg-gray-500 transition-colors"
          >
            Cancel
          </button>
          <button
            disabled={loading}
            onClick={handleGenerate}
            className={`px-6 py-2 bg-blue-600 text-white rounded font-bold border-none transition-colors cursor-pointer ${loading ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-700"
              }`}
          >
            {loading ? "Generating..." : "Generate Logs"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenerateEmployeeFixedLogs;